import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { queryClient } from '../api/queryClient'
import api from '../api/client'
import { getActiveMenu, getActiveSubmenu } from '../config/navigation'
import { resolveMediaUrl } from '../utils/mediaUrl'
import { PERMISSION_DENIED_EVENT, SUBSCRIPTION_EXPIRED_EVENT, SUBSCRIPTION_ACTIVATED_EVENT } from '../utils/permissionDeniedEvents'
import { guardCreateAction } from '../utils/permissionActionGuard'
import { parseStoredUser, getRequiredModuleForPath, getCurrentUserId } from '../utils/authSession'
import { checkModuleEntitlement, normalizeModuleCode } from '../utils/moduleAccess'
import { useAuth } from '../hooks/useAuth'
import AccessDeniedModal from '../components/AccessDeniedModal'
import SubscriptionUpgradeBanner from '../components/SubscriptionUpgradeBanner'
import LogoutModal from './components/LogoutModal'
import SidebarFooterLinks from './components/SidebarFooterLinks'
import TopbarAvatarMenu from './components/TopbarAvatarMenu'
import TopbarSubmenuCanopy from './components/TopbarSubmenuCanopy'
import { useProctoringStatusMonitor } from './hooks/useProctoringStatusMonitor'
import { getUnreadIndicators } from '../features/support-ticket/api/supportTicketApi'
import { SHARED_MASTERS_REGISTRY } from '../features/masters/generic-master/sharedMastersRegistry'
import { TelephonyProvider, useTelephony } from '../features/telephony/context/TelephonyContext'
import ActiveCallHeaderDock from '../components/ActiveCallHeaderDock'
import { useServiceWorker, showServiceWorkerNotification } from '../hooks/useServiceWorker'
import './DashboardLayout.css'

function GlobalActiveCallDock() {
  const {
    isCallActive,
    callState,
    activeCallerInfo,
    callDurationSecs,
    isMuted,
    isOnHold,
    isDockedToHeader,
    expandFromDock,
    endCall,
    toggleMute,
    toggleHold,
  } = useTelephony()
  const location = useLocation()

  // Only render top dock for active, established calls.
  // During RINGING or CONNECTING, the agent's focus remains on the Inbound Call Drawer.
  const shouldShowDock =
    isCallActive &&
    callState === 'CONNECTED' &&
    (isDockedToHeader || location.pathname !== '/call-center')

  if (!shouldShowDock) return null

  return (
    <ActiveCallHeaderDock
      isActive={true}
      callerInfo={activeCallerInfo}
      callDirection={activeCallerInfo?.direction?.toUpperCase() || 'OUTBOUND'}
      callerName={activeCallerInfo?.customer_name || 'Customer'}
      callerNumber={activeCallerInfo?.from || ''}
      callDurationSecs={callDurationSecs}
      isMuted={isMuted}
      isOnHold={isOnHold}
      onExpand={expandFromDock}
      onExpandDrawer={expandFromDock}
      onHangup={endCall}
      onEndCall={endCall}
      onToggleMute={toggleMute}
      onToggleHold={toggleHold}
    />
  )
}

function getUserIdentity() {
  const storedUser = localStorage.getItem('user')
  if (!storedUser) {
    return { name: 'Alex Mercer', role: 'Principal Architect', avatarUrl: '' }
  }

  try {
    const parsed = JSON.parse(storedUser)
    return {
      name: parsed?.name || parsed?.userName || 'Alex Mercer',
      role: parsed?.designation || parsed?.role || parsed?.roleName || 'Principal Architect',
      avatarUrl: resolveMediaUrl(
        parsed?.profile_photo_path ||
          parsed?.profilePhotoPath ||
          parsed?.profilePhotoUrl ||
          parsed?.profile_photo ||
          parsed?.profile_photo_url ||
          parsed?.avatar ||
          parsed?.photoUrl ||
          parsed?.photo_url ||
          parsed?.image_url,
      ),
    }
  } catch {
    return { name: 'Alex Mercer', role: 'Principal Architect', avatarUrl: '' }
  }
}


function resolveChatWsUrl() {
  const token = localStorage.getItem('authToken')
  if (!token) return ''

  const rawBase = String(import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000').trim().replace(/\/+$/, '')
  try {
    const parsed = new URL(rawBase)
    const protocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:'
    const pathname = `${parsed.pathname.replace(/\/+$/, '')}/chat/ws`
    return `${protocol}//${parsed.host}${pathname}?token=${encodeURIComponent(token)}`
  } catch {
    return ''
  }
}

export default function DashboardLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout, hasPrivilege, isAdmin, menus, getActiveMenu: authGetActiveMenu, getActiveSubmenu: authGetActiveSubmenu } = useAuth()
  const storedUser = parseStoredUser()
  const isEmployee = Boolean(storedUser?.role === 'employee' || storedUser?.is_employee)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const [systemLogoutContext, setSystemLogoutContext] = useState(null)
  const [avatarLoadError, setAvatarLoadError] = useState(false)
  const [userIdentity, setUserIdentity] = useState(() => getUserIdentity())
  const [permissionModal, setPermissionModal] = useState(null)
  const [dismissedRouteModalKey, setDismissedRouteModalKey] = useState('')
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0)
  const [unreadChatCount, setUnreadChatCount] = useState(0)
  const chatWsRef = useRef(null)
  const chatWsReconnectRef = useRef(null)
  const readonlyCleanupRef = useRef(() => {})
  const currentUserId = useMemo(() => getCurrentUserId(), [location.pathname, userIdentity.avatarUrl])
  const [hasSupportIndicator, setHasSupportIndicator] = useState(false)
  const seenLastCommentIdRef = useRef(null) // persists across re-renders without triggering re-renders

  const notifiedNotificationIds = useRef(new Set())
  const isFirstNotificationLoad = useRef(true)
  const unreadNotificationCountRef = useRef(0)
  unreadNotificationCountRef.current = unreadNotificationCount

  // ── Session modules: reactive state so the sidebar/topbar/overlay update
  // immediately when a subscription expires mid-session (soft-block).
  // Initialized from localStorage / JWT on mount; updated by the global
  // SUBSCRIPTION_EXPIRED_EVENT emitted from the Axios response interceptor.
  const [sessionModules, setSessionModules] = useState(
    () => parseStoredUser().modules || [],
  )

  useEffect(() => {
    function onSubscriptionExpired(event) {
      const expiredModule = String(event?.detail?.module || '').toLowerCase()
      if (!expiredModule) return

      // 1. Strip the module from localStorage so page refreshes also reflect it.
      try {
        const raw = localStorage.getItem('user')
        if (raw) {
          const stored = JSON.parse(raw)
          if (Array.isArray(stored?.modules)) {
            stored.modules = stored.modules.filter((m) => m !== expiredModule)
            localStorage.setItem('user', JSON.stringify(stored))
          }
        }
      } catch {
        // localStorage parse failure is non-fatal; state update below still fires.
      }

      // 2. Update React state → triggers re-render of sidebar, topbar, and the
      //    routePermission useMemo (which now depends on sessionModules).
      setSessionModules((prev) => prev.filter((m) => m !== expiredModule))
    }

    function onSubscriptionActivated(event) {
      const newModules = event?.detail?.modules
      if (Array.isArray(newModules) && newModules.length > 0) {
        try {
          const raw = localStorage.getItem('user')
          if (raw) {
            const stored = JSON.parse(raw)
            stored.modules = newModules
            localStorage.setItem('user', JSON.stringify(stored))
          }
        } catch {
          // localStorage parse failure is non-fatal
        }
        setSessionModules(newModules)
      }
    }

    window.addEventListener(SUBSCRIPTION_EXPIRED_EVENT, onSubscriptionExpired)
    window.addEventListener(SUBSCRIPTION_ACTIVATED_EVENT, onSubscriptionActivated)
    return () => {
      window.removeEventListener(SUBSCRIPTION_EXPIRED_EVENT, onSubscriptionExpired)
      window.removeEventListener(SUBSCRIPTION_ACTIVATED_EVENT, onSubscriptionActivated)
    }
  }, []) // runs once — the handler closes over setSessionModules (stable ref)

  useServiceWorker({
    onNavigate: (url) => {
      if (url) navigate(url)
    },
  })


  // Poll support ticket unread indicators every 45 seconds.
  // Also re-checks instantly when the user reads a ticket comment (via custom event).
  useEffect(() => {
    if (isEmployee) return undefined
    let cancelled = false
    const LS_KEY = 'bridge_read_ticket_comments'

    const getReadComments = () => {
      try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}') } catch { return {} }
    }

    const check = async () => {
      const token = localStorage.getItem('authToken')
      if (!token) return
      try {
        const tickets = await getUnreadIndicators()
        if (cancelled) return
        const uid = getCurrentUserId()
        const readComments = getReadComments()
        // Show dot if ANY ticket has a last comment that:
        //  1. exists
        //  2. was NOT written by the current user
        //  3. has NOT already been read (id is higher than stored read id)
        const hasNew = tickets.some((t) => {
          if (typeof t.hasUnseen === 'boolean') return t.hasUnseen
          if (!t.lastCommentId) return false
          if (t.lastCommentUserId === uid) return false
          const lastReadId = Number(readComments[t.id] || 0)
          return lastReadId < Number(t.lastCommentId)
        })
        setHasSupportIndicator(hasNew)
      } catch {
        // silently ignore — backend may be unreachable
      }
    }

    // Re-check immediately when a comment is marked read inside the management screen
    const onCommentsRead = () => {
      // Re-run the indicator logic immediately with the updated localStorage values.
      // We already have `tickets` from the last poll, but we don't have them here,
      // so just re-trigger the full async check — it's a tiny lightweight request.
      check()
    }

    check()
    const timer = setInterval(check, 45_000)
    window.addEventListener('bridge-support-comments-read', onCommentsRead)
    return () => {
      cancelled = true
      clearInterval(timer)
      window.removeEventListener('bridge-support-comments-read', onCommentsRead)
    }
  }, [])



  const performClientLogout = useCallback(async (stopReason = null) => {
    try {
      window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason: stopReason } }))
    } catch (e) {
      console.warn('Failed to dispatch auth:logout event:', e)
    }

    const agentPort = sessionStorage.getItem('agentPort')
    if (agentPort) {
      try {
        await fetch(`http://localhost:${agentPort}/stop-tracking`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: stopReason }),
        })
      } catch {
        // Agent may be unreachable.
      }
    }

    const storedUser = parseStoredUser()
    const isEmp = storedUser?.role === 'employee' || storedUser?.is_employee
    sessionStorage.removeItem('agentPort')
    sessionStorage.removeItem('sessionId')
    localStorage.removeItem('user')
    logout()
    queryClient.clear()
    navigate(isEmp ? '/self-service/login' : '/login', { replace: true })
  }, [logout, navigate])

  const forceLogoutForInactivity = useCallback(
    async (reason = 'USER_INACTIVE', context = null) => {
      if (isEmployee) return
      const normalizedReason = String(reason || 'USER_INACTIVE').toUpperCase()
      if (normalizedReason === 'SYSTEM_FAILURE' || normalizedReason === 'NO_SESSION') {
        setSystemLogoutContext({
          reason: normalizedReason,
          context: context || null,
        })
        return
      }

      try {
        await api.post('/activity-monitor/logout', { reason: 'USER_INACTIVE' })
      } catch {
        // Best effort.
      }
      await performClientLogout('USER_INACTIVE')
    },
    [performClientLogout],
  )

  const {
    isProctoringActive,
    showWarning,
    countdown,
    dismissWarning,
    sessionStatusSnapshot,
  } = useProctoringStatusMonitor({
    onSessionEnded: forceLogoutForInactivity,
    enabled: !isEmployee,
  })

  useEffect(() => {
    setUserIdentity(getUserIdentity())
  }, [location.pathname])

  useEffect(() => {
    if (isEmployee) return undefined
    const wsUrl = resolveChatWsUrl()
    if (!wsUrl) return undefined

    let disposed = false
    window.__chatWsConnected = false
    window.dispatchEvent(new CustomEvent('chat-ws-status', { detail: { connected: false } }))
    const onWsSend = (event) => {
      const payload = event?.detail
      if (!payload || typeof payload !== 'object') return
      const ws = chatWsRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN) return
      try {
        ws.send(JSON.stringify(payload))
      } catch {
        // ignore
      }
    }
    window.addEventListener('chat-ws-send', onWsSend)

    const connect = () => {
      const ws = new WebSocket(wsUrl)
      chatWsRef.current = ws

      ws.onopen = () => {
        window.__chatWsConnected = true
        window.dispatchEvent(new CustomEvent('chat-ws-status', { detail: { connected: true } }))
      }

      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data)
          if (!payload || typeof payload !== 'object') return

          if (payload.event === 'new_message') {
            window.dispatchEvent(new CustomEvent('chat-unread-updated'))
            
            const senderId = Number(payload.message?.sender_id || 0)
            const activeUserId = getCurrentUserId()
            console.log('Bridge Chat WS: New message event received:', {
              senderId,
              activeUserId,
              isSenderDifferent: senderId !== activeUserId,
              visibilityState: document.visibilityState,
              pathname: window.location.pathname,
              search: window.location.search
            })
            if (senderId > 0 && senderId !== activeUserId) {
              const isTabInactive = document.visibilityState !== 'visible'
              const isOnDifferentScreen = !window.location.pathname.startsWith('/bridge/conversation') && !window.location.pathname.startsWith('/communication/conversation')
              const queryParams = new URLSearchParams(window.location.search)
              const activeChannelId = queryParams.get('channelId')
              const isOnDifferentThread = (window.location.pathname.startsWith('/bridge/conversation') || window.location.pathname.startsWith('/communication/conversation')) && activeChannelId !== String(payload.channelId)

              console.log('Bridge Chat WS: Evaluating notification visibility:', {
                isTabInactive,
                isOnDifferentScreen,
                isOnDifferentThread,
                shouldNotify: isTabInactive || isOnDifferentScreen || isOnDifferentThread
              })
              if (isTabInactive || isOnDifferentScreen || isOnDifferentThread) {
                const isFile = String(payload.message?.message_type || '').toUpperCase() === 'FILE'
                const body = isFile ? `[Attachment] ${payload.message?.file_name || 'File'}` : (payload.message?.content || '')
                const title = payload.message?.sender_name ? `Message from ${payload.message.sender_name}` : 'New Message'
                showServiceWorkerNotification({
                  title,
                  body,
                  urlToOpen: `/bridge/conversation?channelId=${payload.channelId}`,
                  tag: `chat-${payload.channelId}-${payload.message?.id || Date.now()}`,
                })
              }
            }
          } else if (payload.event === 'read_receipt') {
            window.dispatchEvent(new CustomEvent('chat-unread-updated'))
          } else if (payload.event === 'new_notification' && payload.notification) {
            console.log('Bridge Chat WS: New system notification received:', payload.notification)
            const notif = payload.notification
            if (notif.id) {
              notifiedNotificationIds.current.add(notif.id)
            }
            window.dispatchEvent(new CustomEvent('notifications-updated'))
            showServiceWorkerNotification({
              title: notif.title || 'Notification',
              body: notif.message || '',
              urlToOpen: notif.urlToOpen || '/notifications',
              tag: `notif-${notif.id || 'alert'}-${Date.now()}`,
            })
          }

          if (
            payload.event === 'new_message'
            || payload.event === 'read_receipt'
            || payload.event === 'typing'
            || payload.event === 'stop_typing'
          ) {
            window.dispatchEvent(new CustomEvent('chat-ws-event', { detail: payload }))
          }
        } catch {
          // ignore malformed payload
        }
      }

      ws.onclose = () => {
        window.__chatWsConnected = false
        window.dispatchEvent(new CustomEvent('chat-ws-status', { detail: { connected: false } }))
        if (disposed) return
        chatWsReconnectRef.current = window.setTimeout(connect, 3000)
      }

      ws.onerror = () => {
        window.__chatWsConnected = false
        window.dispatchEvent(new CustomEvent('chat-ws-status', { detail: { connected: false } }))
      }
    }

    connect()

    return () => {
      disposed = true
      window.removeEventListener('chat-ws-send', onWsSend)
      if (chatWsReconnectRef.current) {
        window.clearTimeout(chatWsReconnectRef.current)
        chatWsReconnectRef.current = null
      }

      const ws = chatWsRef.current
      if (ws) {
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CLOSING) {
          try {
            ws.close()
          } catch {
            // ignore
          }
        } else if (ws.readyState === WebSocket.CONNECTING) {
          ws.onopen = () => {
            try {
              ws.close()
            } catch {
              // ignore
            }
          }
        }
      }
      chatWsRef.current = null
      window.__chatWsConnected = false
      window.dispatchEvent(new CustomEvent('chat-ws-status', { detail: { connected: false } }))
    }
  }, [])

  useEffect(() => {
    if (isEmployee) return undefined
    let active = true
    let timerId = null

    async function refreshChatUnread() {
      try {
        const { data } = await api.get('/chat/unread-summary')
        if (!active) return
        setUnreadChatCount(Number(data?.unreadTotal || 0))
      } catch {
        if (!active) return
        setUnreadChatCount(0)
      }
    }

    refreshChatUnread()
    timerId = window.setInterval(refreshChatUnread, 30000)
    window.addEventListener('focus', refreshChatUnread)
    window.addEventListener('chat-unread-updated', refreshChatUnread)
    return () => {
      active = false
      if (timerId) window.clearInterval(timerId)
      window.removeEventListener('focus', refreshChatUnread)
      window.removeEventListener('chat-unread-updated', refreshChatUnread)
    }
  }, [location.pathname])

  useEffect(() => {
    let active = true

    async function hydrateProfile() {
      const token = localStorage.getItem('authToken')
      if (!token) return
      try {
        const { data } = await api.get('/auth/me')
        if (!active || !data) return
        const current = (() => {
          try {
            return JSON.parse(localStorage.getItem('user') || '{}')
          } catch {
            return {}
          }
        })()
        const nextUser = {
          ...current,
          ...data,
        }
        localStorage.setItem('user', JSON.stringify(nextUser))
        setUserIdentity(getUserIdentity())
        if (Array.isArray(data?.modules)) {
          setSessionModules(data.modules)
          window.dispatchEvent(
            new CustomEvent(SUBSCRIPTION_ACTIVATED_EVENT, { detail: { modules: data.modules } }),
          )
        }
      } catch {
        // Keep local fallback identity when profile hydration fails.
      }
    }

    hydrateProfile()
    return () => {
      active = false
    }
  }, [location.pathname])

  useEffect(() => {
    setAvatarLoadError(false)
  }, [userIdentity.avatarUrl])

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow
    const previousHtmlOverflow = document.documentElement.style.overflow
    const previousBodyHeight = document.body.style.height
    const previousHtmlHeight = document.documentElement.style.height

    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    document.body.style.height = '100%'
    document.documentElement.style.height = '100%'

    return () => {
      document.body.style.overflow = previousBodyOverflow
      document.documentElement.style.overflow = previousHtmlOverflow
      document.body.style.height = previousBodyHeight
      document.documentElement.style.height = previousHtmlHeight
    }
  }, [])

  useEffect(() => {
    if (isEmployee) return undefined
    let active = true

    async function refreshUnreadCount() {
      try {
        const { data } = await api.get('/notifications/summary')
        if (!active) return
        setUnreadNotificationCount(Number(data?.unreadCount || 0))
      } catch {
        if (!active) return
        setUnreadNotificationCount(0)
      }
    }

    function onVisibilityRefresh() {
      if (document.visibilityState === 'visible') {
        refreshUnreadCount()
      }
    }

    refreshUnreadCount()
    window.addEventListener('focus', refreshUnreadCount)
    window.addEventListener('notifications-updated', refreshUnreadCount)
    document.addEventListener('visibilitychange', onVisibilityRefresh)

    return () => {
      active = false
      window.removeEventListener('focus', refreshUnreadCount)
      window.removeEventListener('notifications-updated', refreshUnreadCount)
      document.removeEventListener('visibilitychange', onVisibilityRefresh)
    }
  }, [location.pathname])

  // Instantly refresh navbar avatar when current user updates their profile photo.
  useEffect(() => {
    function onProfileUpdated() {
      setAvatarLoadError(false)
      setUserIdentity(getUserIdentity())
    }
    window.addEventListener('user-profile-updated', onProfileUpdated)
    return () => window.removeEventListener('user-profile-updated', onProfileUpdated)
  }, [])

  const activeMenu = useMemo(() => authGetActiveMenu(location.pathname) || {}, [authGetActiveMenu, location.pathname])
  const activeSubmenu = useMemo(() => authGetActiveSubmenu(location.pathname) || {}, [authGetActiveSubmenu, location.pathname])
  const isCustomMaster = useMemo(() => {
    if (!activeSubmenu) return false;
    const isMasterSection = activeSubmenu.section === 'masters' || location.pathname.startsWith('/masters/');
    const isGenericMaster = activeSubmenu.id in SHARED_MASTERS_REGISTRY || activeSubmenu.module in SHARED_MASTERS_REGISTRY;
    const isImplementedAcHead = [
      '/masters/acheads/heads',
      '/masters/acheads/vendors',
      '/masters/acheads/banks',
      '/masters/acheads/employees',
      '/masters/acheads/customers',
      '/masters/acheads/cust-cash'
    ].some(path => location.pathname.startsWith(path));
    return isMasterSection && !isGenericMaster && !isImplementedAcHead;
  }, [activeSubmenu, location.pathname]);
  const isSupportRoute = location.pathname.startsWith('/support')
  const isCommunicationConversationRoute = location.pathname.startsWith('/bridge/conversation') || location.pathname.startsWith('/communication/conversation')
  const isNotificationsRoute = location.pathname.startsWith('/notifications')
  const isEmailSettingsRoute = location.pathname.startsWith('/settings/email-notifications')
  const isReportScheduleRoute = location.pathname.startsWith('/reports/schedules')
  const isReportHomeRoute = location.pathname.startsWith('/reports') && !isReportScheduleRoute
  const createTarget = useMemo(() => {
    if (activeSubmenu?.type === 'custom' || activeSubmenu?.type === 'report' || activeSubmenu?.hideCreate || location.pathname.startsWith('/cloud/feedback')) {
      return null;
    }
    if (location.pathname.endsWith('/list')) {
      return activeSubmenu?.entryPath || location.pathname.replace(/\/list$/, '/entry');
    }
    return activeSubmenu?.entryPath || null;
  }, [location.pathname, activeSubmenu]);

  const hasModule = (name) => checkModuleEntitlement(name, sessionModules)

  // True when user ONLY has education module and no corporate suite modules
  const isEducationOnlyUser = useMemo(() => {
    const corpModules = ['bruno', 'nerve', 'bridge', 'loop', 'analytics', 'reports', 'audit_logs']
    const normalized = new Set((sessionModules || []).map(normalizeModuleCode))
    const hasCorp = corpModules.some((m) => normalized.has(m))
    return normalized.has('education') && !hasCorp
  }, [sessionModules])

  // Sidebar: map each top-level menu to its required subscription module
  const MENU_MODULE_MAP = {
    lectures: 'education',
    attendance: 'education',
    remarks: 'education',
    cloud: 'education',
    productivity: 'bruno',
    bruno: 'bruno',
    'project-management': 'nerve',
    nerve: 'nerve',
    communication: 'bridge',
    bridge: 'bridge',
    telephony: 'loop',
    loop: 'loop',
    payroll: 'payroll',
    analytics: 'analytics',
    reports: 'reports',
    'audit-logs': 'audit_logs',
  }

  const reportTopTabs = [
    { id: 'all', label: 'All Reports', enabled: true, to: '/reports/masters', active: isReportHomeRoute },
    { id: 'scheduled', label: 'Schedule Reports', enabled: true, to: '/reports/schedules/list', active: isReportScheduleRoute },
    { id: 'history', label: 'Export History', enabled: false, to: null, active: false },
  ]

  const emailSettingsTopTabs = [
    {
      id: 'workflows',
      label: 'Approval Workflows',
      to: '/settings/email-notifications/workflows',
      active: location.pathname.includes('/workflows') || location.pathname === '/settings/email-notifications',
    },
    {
      id: 'templates',
      label: 'Email Templates',
      to: '/settings/email-notifications/templates',
      active: location.pathname.includes('/templates'),
    },
    {
      id: 'audit-logs',
      label: 'Delivery Audit Logs',
      to: '/settings/email-notifications/audit-logs',
      active: location.pathname.includes('/audit-logs'),
    },
    {
      id: 'account',
      label: 'Email Account',
      to: '/settings/email-notifications/account',
      active: location.pathname.includes('/account'),
    },
  ]

  const handleLogout = () => {
    // Open the logout modal with reason selection
    setShowLogoutModal(true)
  }

  const handleOpenAccount = () => {
    const storedUser = parseStoredUser()
    if (storedUser?.role === 'employee' || storedUser?.is_employee) {
      navigate('/self-service/my-profile')
      return
    }
    if (!currentUserId) return
    navigate(`/masters/users/entry?id=${currentUserId}`)
  }

  const handlePermissionModalClose = useCallback(() => {
    setPermissionModal(null)
  }, [])

  useEffect(() => {
    const onPermissionDenied = (event) => {
      const detail = event?.detail || {}
      const action = String(detail?.action || '').toLowerCase()
      if (action !== 'view' && action !== 'create' && action !== 'edit') return
      setPermissionModal({
        action,
        routeLabel: String(detail?.routeLabel || ''),
      })
    }
    window.addEventListener(PERMISSION_DENIED_EVENT, onPermissionDenied)
    return () => window.removeEventListener(PERMISSION_DENIED_EVENT, onPermissionDenied)
  }, [])

  const modalMode = systemLogoutContext
    ? 'system-failure'
    : showLogoutModal
      ? 'manual'
      : 'idle-warning'
  const isLogoutModalVisible = Boolean(systemLogoutContext) || showLogoutModal || showWarning
  const handleCloseLogoutModal = () => {
    if (systemLogoutContext) return
    if (showLogoutModal) {
      setShowLogoutModal(false)
      return
    }
    dismissWarning()
  }

  return (
    <TelephonyProvider>
      <div className="bridge-shell">
      <LogoutModal
        isOpen={isLogoutModalVisible}
        onClose={handleCloseLogoutModal}
        mode={modalMode}
        onSystemFailureAcknowledge={() => performClientLogout('SYSTEM_FAILURE')}
        idleContext={{
          countdown,
          sessionStatus: sessionStatusSnapshot,
        }}
        systemContext={systemLogoutContext}
      />
      <AccessDeniedModal
        open={Boolean(permissionModal)}
        action={permissionModal?.action || 'view'}
        routeLabel={permissionModal?.routeLabel || ''}
        onClose={handlePermissionModalClose}
      />

      <aside className={`bridge-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-brand">
          <div className="brand-icon">
            <span className="material-symbols-outlined fill">link</span>
          </div>
          <div>
            <h2>{parseStoredUser()?.role === 'employee' || parseStoredUser()?.is_employee ? 'Self-Service' : 'Boss Global'}</h2>
            <p>{parseStoredUser()?.role === 'employee' || parseStoredUser()?.is_employee ? 'Employee Portal' : 'Enterprise Management'}</p>
          </div>
        </div>

        <nav className="sidebar-nav-block app-hide-scrollbar" aria-label="Main Modules">
          {menus.map((menu) => {
            const isMenuActive = activeMenu.id === menu.id
            const isDisabled = menu.enabled === false
            return (
              <button
                key={menu.id}
                type="button"
                className={`menu-btn ${isMenuActive ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                disabled={isDisabled}
                aria-disabled={isDisabled}
                onClick={() => {
                  if (isDisabled) return
                  navigate(menu.defaultPath)
                  setSidebarOpen(false)
                }}
              >
                <span className="menu-icon-wrap">
                  <span className="material-symbols-outlined">{menu.icon}</span>
                  {(menu.id === 'communication' || menu.id === 'bridge') && unreadChatCount > 0 ? (
                    <span className="menu-icon-badge" aria-label={`${unreadChatCount} unread chats`}>
                      {unreadChatCount > 99 ? '99+' : unreadChatCount}
                    </span>
                  ) : null}
                </span>
                <span>{menu.label}</span>
              </button>
            )
          })}
        </nav>

        <SidebarFooterLinks
          onSupportNavigate={() => {
            navigate('/support/tickets/list')
            setSidebarOpen(false)
          }}
          isSupportActive={isSupportRoute}
          onLogout={handleLogout}
          hasSupportIndicator={hasSupportIndicator}
        />
      </aside>

      <main className="bridge-main">
        <SubscriptionUpgradeBanner />
        {!isCommunicationConversationRoute ? (
          <header className="bridge-topbar">
            <div className="topbar-left">
              <button
                type="button"
                className="mobile-nav-trigger"
                onClick={() => setSidebarOpen((prev) => !prev)}
                aria-label="Toggle navigation"
              >
                <span className="material-symbols-outlined">menu</span>
              </button>

              {/* <span className="top-brand">BRIDGE</span> */}

              {isNotificationsRoute ? (
                <TopbarSubmenuCanopy
                  menu={activeMenu}
                  activeSubmenuId={activeSubmenu?.id}
                  isNotificationsRoute={true}
                  isReportMenu={false}
                  reportTopTabs={reportTopTabs}
                  isEmailSettingsRoute={false}
                  emailSettingsTopTabs={[]}
                  hasModule={hasModule}
                  getRequiredModule={getRequiredModuleForPath}
                />
              ) : isEmailSettingsRoute ? (
                <TopbarSubmenuCanopy
                  menu={activeMenu}
                  activeSubmenuId={activeSubmenu?.id}
                  isNotificationsRoute={false}
                  isReportMenu={false}
                  reportTopTabs={[]}
                  isEmailSettingsRoute={true}
                  emailSettingsTopTabs={emailSettingsTopTabs}
                  hasModule={hasModule}
                  getRequiredModule={getRequiredModuleForPath}
                />
              ) : (
                <TopbarSubmenuCanopy
                  menu={activeMenu}
                  activeSubmenuId={activeSubmenu?.id}
                  isNotificationsRoute={false}
                  isReportMenu={activeMenu?.id === 'reports'}
                  reportTopTabs={reportTopTabs}
                  isEmailSettingsRoute={false}
                  emailSettingsTopTabs={[]}
                  hasModule={hasModule}
                  getRequiredModule={getRequiredModuleForPath}
                />
              )}
            </div>

            <div className="topbar-right">
              {createTarget && (activeSubmenu?.canCreate ?? (isAdmin || !activeSubmenu?.createPrivilege || hasPrivilege(activeSubmenu?.createPrivilege))) ? (
                <button
                  type="button"
                  className="create-btn"
                  onClick={() => {
                    if (!createTarget) return
                    const guardResult = guardCreateAction(createTarget, activeSubmenu?.label || '', activeSubmenu?.createPrivilege)
                    if (!guardResult.allowed) return
                    navigate(createTarget)
                  }}
                >
                  <span className="material-symbols-outlined">add</span>
                  <span>Create New</span>
                </button>
              ) : null}

              {!isEmployee && (
                <>
                  <button
                    type="button"
                    className="icon-btn notification-icon-btn"
                    aria-label="Notifications"
                    onClick={() => navigate('/notifications')}
                  >
                    <span className="menu-icon-wrap">
                      <span className="material-symbols-outlined">notifications</span>
                      {unreadNotificationCount > 0 ? (
                        <span className="menu-icon-badge" aria-label={`${unreadNotificationCount} unread notifications`}>
                          {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                        </span>
                      ) : null}
                    </span>
                  </button>

                  <button type="button" className="icon-btn" aria-label="Help">
                    <span className="material-symbols-outlined">help_outline</span>
                  </button>
                </>
              )}

              <div className="topbar-identity">
                <p className="profile-name">{userIdentity.name || 'User'}</p>
                <p className="profile-role">{userIdentity.role || 'Member'}</p>
              </div>

              <TopbarAvatarMenu
                userIdentity={userIdentity}
                avatarLoadError={avatarLoadError}
                onAvatarError={() => setAvatarLoadError(true)}
                isProctoringActive={isProctoringActive}
                onAccount={handleOpenAccount}
                accountDisabled={!currentUserId}
                isEmployee={Boolean(parseStoredUser()?.role === 'employee' || parseStoredUser()?.is_employee)}
                onSubscriptions={() => navigate('/masters/subscriptions')}
                onSettings={() => navigate('/settings/email-notifications')}
                onLogout={() => {
                  if (isEmployee) {
                    performClientLogout('USER_MANUAL')
                  } else {
                    setShowLogoutModal(true)
                  }
                }}
              />
            </div>
          </header>
        ) : null}

        <GlobalActiveCallDock />

        <section
          className={`bridge-canvas ${isCommunicationConversationRoute ? 'bridge-canvas--conversation' : ''}`}
        >
          <Outlet />
        </section>
      </main>
    </div>
    </TelephonyProvider>
  )
}
